<div class="tdbf-page">
  <div class="tdbf-save-bar">
    <div class="tdbf-save-bar__inner">
      <div class="tdbf-save-bar__left">
        <button
          type="button"
          class="tdbf-save-bar__back"
          [attr.aria-label]="'testdatenblaetter.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tdbf-save-bar__heading">
          <span class="tdbf-save-bar__eyebrow">
            @if (isEdit) {
              {{ 'testdatenblaetter.form.eyebrowExisting' | translate: { id: tdbLabel() } }}
            } @else {
              {{ 'testdatenblaetter.form.eyebrowNew' | translate }}
            }
          </span>
          <span class="tdbf-save-bar__title">
            {{ (isEdit ? 'testdatenblaetter.form.editTitle' : 'testdatenblaetter.form.createTitle') | translate }}
          </span>
        </div>
        @if (form.dirty) {
          <span class="tdbf-dirty-pill">
            <span class="tdbf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testdatenblaetter.form.dirty' | translate }}
          </span>
        }
      </div>
      <div class="tdbf-save-bar__right">
        @if (!isReadOnly()) {
          <button mat-stroked-button type="button" class="tdbf-csv-btn" disabled>
            <mat-icon aria-hidden="true">upload</mat-icon>
            {{ 'testdatenblaetter.form.importCsv' | translate }}
          </button>
        }
        <button mat-stroked-button type="button" (click)="onCancel()">
          {{ 'testdatenblaetter.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button mat-flat-button color="primary" type="button" [disabled]="saveDisabled()" (click)="onSave()">
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testdatenblaetter.form.save' : 'testdatenblaetter.form.saveNew') | translate }}
          </button>
        }
      </div>
    </div>
  </div>

  @if (isReadOnly()) {
    <div class="tdbf-readonly-hint">
      <mat-icon class="tdbf-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="tdbf-readonly-hint__text">{{ 'testdatenblaetter.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="tdbf-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="tdbf-page__error" role="alert">{{ 'testdatenblaetter.form.loadError' | translate }}</p>
  } @else {
    <div class="tdbf-body">
      @if (saveError()) {
        <p class="tdbf-hint tdbf-hint--error" role="alert" aria-live="polite">
          {{ 'testdatenblaetter.form.saveError' | translate }}
        </p>
      }

      <!-- Grunddaten -->
      <div class="tdbf-card" [formGroup]="form">
        <div class="tdbf-card__body">
          <to-section-title
            [title]="'testdatenblaetter.form.section.basics' | translate"
            [subtitle]="'testdatenblaetter.form.section.basicsSubtitle' | translate"
          />
          <div class="tdbf-fields">
            <mat-form-field appearance="outline" floatLabel="always" class="tdbf-field tdbf-field--uppercase-label">
              <mat-label>{{ 'testdatenblaetter.form.field.name' | translate }}</mat-label>
              <input
                matInput
                formControlName="name"
                [attr.maxlength]="fieldLengths.NAME_MAX"
                [placeholder]="'testdatenblaetter.form.field.namePlaceholder' | translate"
              />
              @if (form.controls.name.hasError('required') && form.controls.name.touched) {
                <mat-error>{{ 'testdatenblaetter.form.field.nameRequired' | translate }}</mat-error>
              }
            </mat-form-field>
            <app-person-select
              class="tdbf-field"
              formControlName="assigneeUserId"
              [label]="'testdatenblaetter.form.field.assignee' | translate"
              [options]="personOptions()"
              [allowNone]="false"
              [required]="true"
              noneKey="testdatenblaetter.form.field.noAssignee"
            ></app-person-select>
            @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
              <p class="tdbf-field-error" role="alert">
                {{ 'testdatenblaetter.form.field.assigneeRequired' | translate }}
              </p>
            }
            <mat-form-field
              appearance="outline"
              floatLabel="always"
              class="tdbf-field tdbf-field--uppercase-label tdbf-field--full"
            >
              <mat-label>{{ 'testdatenblaetter.form.field.description' | translate }}</mat-label>
              <textarea
                matInput
                rows="2"
                formControlName="beschreibung"
                [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                [placeholder]="'testdatenblaetter.form.field.descriptionPlaceholder' | translate"
              ></textarea>
              <mat-hint align="end" aria-live="polite">
                {{ form.controls.beschreibung.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
              </mat-hint>
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Daten (Spreadsheet) -->
      <div class="tdbf-card">
        <div class="tdbf-card__body">
          <div class="tdbf-card__head">
            <div class="tdbf-card__head-text">
              <to-section-title
                data-testid="tdbf-data-subtitle"
                [title]="'testdatenblaetter.form.section.data' | translate"
                [subtitle]="
                  'testdatenblaetter.form.section.dataSubtitle'
                    | translate
                      : {
                          variables: dataVariablesKey() | translate: { count: cols().length },
                          records: dataRecordsKey() | translate: { count: rows().length },
                        }
                "
              />
            </div>
            @if (!isReadOnly()) {
              <div class="tdbf-card__head-actions">
                <button type="button" class="tdbf-btn tdbf-btn--ghost" (click)="addCol()">
                  <mat-icon aria-hidden="true">add</mat-icon>
                  {{ 'testdatenblaetter.form.data.addColumn' | translate }}
                </button>
                <button type="button" class="tdbf-btn tdbf-btn--primary" (click)="addRow()">
                  <mat-icon aria-hidden="true">add</mat-icon>
                  {{ 'testdatenblaetter.form.data.addRow' | translate }}
                </button>
              </div>
            }
          </div>

          <div class="tdbf-grid-wrap">
            <table class="tdbf-grid">
              <thead>
                <tr>
                  <th class="tdbf-grid__index-head">#</th>
                  @for (col of cols(); track $index) {
                    <th class="tdbf-grid__col-head">
                      <div class="tdbf-grid__col-head-inner">
                        <input
                          class="tdbf-grid__col-input"
                          [value]="col"
                          [disabled]="isReadOnly()"
                          [attr.aria-label]="
                            'testdatenblaetter.form.data.columnNameAria' | translate: { index: $index + 1 }
                          "
                          (input)="renameCol($index, $any($event.target).value)"
                        />
                        @if (!isReadOnly()) {
                          <button
                            type="button"
                            class="tdbf-grid__col-del"
                            [disabled]="cols().length <= 1"
                            [attr.aria-label]="'testdatenblaetter.form.data.removeColumn' | translate"
                            (click)="delCol($index)"
                          >
                            <mat-icon aria-hidden="true">close</mat-icon>
                          </button>
                        }
                      </div>
                    </th>
                  }
                  <th class="tdbf-grid__spacer-head"></th>
                </tr>
              </thead>
              <tbody>
                @for (row of rows(); track $index; let ri = $index) {
                  <tr>
                    <td class="tdbf-grid__index-cell">{{ ri + 1 }}</td>
                    @for (cell of row; track $index; let ci = $index) {
                      @let locked = isEncrypted(cell);
                      @let key = cellKey(ri, ci);
                      <td class="tdbf-grid__cell">
                        <div class="tdbf-grid__cell-inner">
                          <input
                            class="tdbf-grid__cell-input"
                            [class.tdbf-grid__cell-input--locked]="locked"
                            [attr.data-testid]="'tdbf-cell-' + key"
                            autocomplete="off"
                            [value]="locked ? encryptedDisplay : cell"
                            [readOnly]="locked"
                            [disabled]="isReadOnly()"
                            [attr.aria-label]="
                              locked
                                ? ('testdatenblaetter.form.data.encryptedCell' | translate)
                                : ('testdatenblaetter.form.data.cellAria' | translate: { row: ri + 1, col: ci + 1 })
                            "
                            (click)="startReentry(ri, ci)"
                            (keydown.enter)="startReentry(ri, ci)"
                            (keydown.escape)="cancelReentry(ri, ci)"
                            (blur)="endReentry(ri, ci)"
                            (input)="setCell(ri, ci, $any($event.target).value)"
                          />
                          @if (!isReadOnly() && !locked) {
                            <button
                              type="button"
                              class="tdbf-grid__cell-encrypt"
                              [attr.data-testid]="'tdbf-cell-encrypt-' + key"
                              [disabled]="!cell || encryptingCell() !== null"
                              [attr.aria-busy]="encryptingCell() === key"
                              [attr.aria-label]="'testdatenblaetter.form.data.encryptCell' | translate"
                              (click)="encryptCell(ri, ci)"
                            >
                              <mat-icon aria-hidden="true">lock</mat-icon>
                            </button>
                          }
                        </div>
                        @if (failedCell() === key) {
                          <p class="tdbf-grid__cell-error" role="alert" [attr.data-testid]="'tdbf-cell-error-' + key">
                            {{ 'secretEncrypt.error' | translate }}
                          </p>
                        }
                      </td>
                    }
                    <td class="tdbf-grid__row-del-cell">
                      @if (!isReadOnly()) {
                        <button
                          type="button"
                          class="tdbf-grid__row-del"
                          [disabled]="rows().length <= 1"
                          [attr.aria-label]="'testdatenblaetter.form.data.removeRow' | translate"
                          (click)="delRow(ri)"
                        >
                          <mat-icon aria-hidden="true">delete</mat-icon>
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="tdbf-info">
            <mat-icon class="tdbf-info__icon" aria-hidden="true">content_copy</mat-icon>
            <span>{{ 'testdatenblaetter.form.data.info' | translate: { count: rows().length } }}</span>
          </div>
        </div>
      </div>
    </div>
  }
</div>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'testdatenblaetter.form.unsavedMessage' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
